import { CommonModule, DatePipe } from '@angular/common';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TranslationService } from '../../../core/i18n/translation.service';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { ChecklistDto } from '../models/checklist.models';
import { ChecklistsApi } from '../services/checklists.api';

type ChecklistViewMode = 'list' | 'tiles';

const CHECKLIST_VIEW_STORAGE_KEY = 'junoaudit.checklists.view-mode';

@Component({
  selector: 'app-checklist-list',
  imports: [CommonModule, DatePipe, ConfirmationDialogComponent],
  templateUrl: './checklist-list.component.html',
  styleUrl: './checklist-list.component.css',
})
export class ChecklistListComponent {
  private readonly api = inject(ChecklistsApi);
  private loadRequestId = 0;

  protected readonly i18n = inject(TranslationService);

  readonly isActive = input(false);
  readonly searchQuery = input('');
  readonly editRequested = output<string>();

  protected readonly isLoading = signal(false);
  protected readonly loadError = signal('');
  protected readonly checklists = signal<ChecklistDto[]>([]);
  protected readonly viewMode = signal<ChecklistViewMode>(this.restoreViewMode());
  protected readonly deleteDialogOpen = signal(false);
  protected readonly checklistPendingDeletion = signal<ChecklistDto | null>(null);
  protected readonly isDeleting = signal(false);

  constructor() {
    effect(() => {
      if (this.isActive()) {
        void this.loadChecklists(this.searchQuery());
      }
    });
  }

  protected setViewMode(mode: ChecklistViewMode): void {
    if (this.viewMode() === mode) {
      return;
    }

    this.viewMode.set(mode);
    localStorage.setItem(CHECKLIST_VIEW_STORAGE_KEY, mode);
  }

  protected trackByChecklistId(_index: number, checklist: ChecklistDto): string {
    return checklist.id;
  }

  protected getStatusLabel(checklist: ChecklistDto): string {
    return this.i18n.t(`checklistCreator.status.${checklist.status}`);
  }

  protected editChecklist(checklist: ChecklistDto): void {
    this.editRequested.emit(checklist.id);
  }

  protected deleteChecklist(checklist: ChecklistDto): void {
    this.checklistPendingDeletion.set(checklist);
    this.deleteDialogOpen.set(true);
  }

  protected async archiveChecklist(checklist: ChecklistDto): Promise<void> {
    if (checklist.status !== 'active') {
      return;
    }

    this.loadError.set('');

    try {
      const updatedChecklist = await firstValueFrom(
        this.api.updateChecklist(checklist.id, { status: 'archived' }),
      );
      this.checklists.update((checklists) =>
        checklists.map((currentChecklist) =>
          currentChecklist.id === updatedChecklist.id ? updatedChecklist : currentChecklist,
        ),
      );
    } catch {
      this.loadError.set(this.i18n.t('checklistList.errors.archive'));
    }
  }

  protected cancelChecklistDeletion(): void {
    if (this.isDeleting()) {
      return;
    }

    this.closeDeleteDialog();
  }

  protected async confirmChecklistDeletion(): Promise<void> {
    const checklist = this.checklistPendingDeletion();
    if (!checklist || this.isDeleting()) {
      return;
    }

    this.isDeleting.set(true);
    this.loadError.set('');

    try {
      await firstValueFrom(this.api.deleteChecklist(checklist.id));
      this.checklists.update((checklists) =>
        checklists.filter((currentChecklist) => currentChecklist.id !== checklist.id),
      );
      this.closeDeleteDialog();
    } catch {
      this.loadError.set(this.i18n.t('checklistList.errors.delete'));
    } finally {
      this.isDeleting.set(false);
    }
  }

  private async loadChecklists(search: string): Promise<void> {
    const requestId = ++this.loadRequestId;
    this.isLoading.set(true);
    this.loadError.set('');

    try {
      const results = await firstValueFrom(this.api.getChecklists(search));
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.checklists.set(results.results);
    } catch {
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.loadError.set(this.i18n.t('checklistList.errors.load'));
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading.set(false);
      }
    }
  }

  private closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
    this.checklistPendingDeletion.set(null);
  }

  private restoreViewMode(): ChecklistViewMode {
    const savedMode = localStorage.getItem(CHECKLIST_VIEW_STORAGE_KEY);
    return savedMode === 'tiles' ? 'tiles' : 'list';
  }
}
